import styled from 'styled-components';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Box from '@mui/material/Box';
import { makeStyles } from '@mui/styles';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import THEME from '../../../../../../constants/theme';
import { ButtonRounded } from '../../../../../atoms';

// Style override for FormHelperText
const useStyles = makeStyles({
  root: {
    '&.Mui-error': {
      color: THEME.secondaryColors.nodeError,
    },
  },
});

const StyledFormControl = styled(FormControl)`
  &.MuiFormControl-root {
    width: 100%;
    height: ${({ $height }) => $height || '40px'};
    background-color: ${({ $bgColor }) => $bgColor || '#f5f6f7'};
    display: flex;
    justify-content: center;
    padding: ${({ $padding }) => $padding || '3px 18px'};
    margin-top: ${({ $mt }) => $mt || '0'};
    margin-bottom: ${({ $mb }) => $mb || '0'};
    box-sizing: border-box;
    border-radius: 6px;
    font-size: 12px;
    position: relative;

    & .MuiInputBase-root {
      margin-top: 2px;
      color: ${THEME.primaryColors.black};

      &:before,
      &:after {
        display: none;
      }
    }

    & .MuiFormHelperText-root.Mui-error {
      position: absolute;
      left: 0;
      bottom: -23px;
    }
  }
`;

const StyledSelect = styled(Select)`
  & .MuiSelect-root {
    padding: ${({ padding }) => padding};

    &.MuiSelect-select {
      &:focus {
        background-color: transparent;
      }
    }
  }

  &.MuiInputBase-root {
    &.Mui-disabled {
      color: #000;

      .MuiSvgIcon-root {
        &.Mui-disabled {
          display: none;
        }
      }
    }

    & .check-icon {
      display: none;
      visibility: hidden;
      opacity: 0;
    }
  }
`;

const ConditionStyledFormControl = styled(FormControl)`
  &.MuiFormControl-root {
    width: 100%;
    padding: 5px;
    background-color: #fff;
    border-radius: 6px;
  }

  & .MuiInputBase-root {
    &:before,
    &:after {
      display: none;
    }
  }
`;

const StyledCopyToClipboard = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $justifyContent }) => $justifyContent || 'space-between'};
  width: 100%;
  color: ${({ $color }) => $color || `${THEME.greyColors.grey1}`};
  font-size: 14px;
  white-space: nowrap;
  overflow-x: scroll;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  margin: 0;
  padding: ${({ $padding }) => $padding || '7.5px 0'};
`;

const StyledCopyValue = styled.p`
  color: ${({ $color }) => $color || `${THEME.greyColors.grey1}`};
  padding: ${({ $padding }) => $padding || '7.5px 0'};
  margin: ${({ $margin }) => $margin || '0'};
  width: ${({ $width }) => $width || '90%'};
  font-size: 14px;
  white-space: nowrap;
  overflow-x: scroll;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledBox = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InfoLinkBox = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${THEME.primaryColors.primaryLight};
  width: 48px;
  height: 48px;
  border-radius: 6px;
`;

const StyledAddNewButton = styled(ButtonRounded)`
  width: 100%;
  height: 48px !important;
  justify-content: flex-start;
  background-color: ${THEME.greyColors.grey12} !important;
  padding: 0 16px !important;

  svg {
    font-size: 16px !important;
    stroke: ${THEME.greyColors.grey1};
    stroke-width: 1px;
  }

  span {
    font-size: 12px;
    font-weight: 600;
    color: ${THEME.greyColors.grey1};
    justify-content: flex-start;
  }

  &:hover {
    background-color: ${THEME.greyColors.grey5} !important;
  }

  &:disabled {
    span {
      color: ${THEME.greyColors.grey8};
    }

    svg {
      stroke: ${THEME.greyColors.grey8};
    }

    &:hover {
      background-color: ${THEME.greyColors.grey12} !important;
    }
  }
`;

const ActionOptionBox = styled(Box)`
  &.MuiBox-root {
    margin-bottom: 8px;
  }
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  margin-bottom: 16px;
`;

const RemoveActionBox = styled(Box)`
  position: absolute;
  right: 10px;
`;

const AddActionButton = styled(ButtonRounded)`
  width: 100%;
  display: flex;
  justify-content: flex-start !important;
  align-items: center;
  height: 48px !important;
  background-color: #f5f6f7 !important;
  font-size: 12px !important;
  color: ${THEME.greyColors.grey1} !important;
  padding: 6px 22px !important;
  &.Mui-disabled {
    color: ${THEME.greyColors.grey8} !important;
  }
`;

const ActionTextField = styled(TextField)`
  & .MuiInputBase-root {
    & .MuiInputBase-input {
      font-size: 12px !important;
      font-weight: 600;
      color: ${THEME.secondaryColors.black2};
    }
  }
`;

const PromptTextField = styled(TextField)`
  & .MuiInputBase-root {
    & .MuiInputBase-input {
      height: 136px !important;
      box-sizing: border-box;
      padding: 8px 0;
    }
  }
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  &.MuiFormControlLabel-root {
    margin: 0 0 7px 0;
    .MuiFormControlLabel-label {
      font-size: 14px;
    }
  }
  &.MuiFormControl-root {
    background: red !important;
  }
  & .MuiFormControl-root {
    background: blue !important;
  }
`;

export const StyledAccordion = styled(Accordion)`
  &.MuiAccordion-root {
    border: none !important;
    padding: 0;
    box-shadow: none !important;
    position: relative;
    z-index: ${({ zIndex }) => zIndex};
    &:before {
      background: transparent;
    }
    &.Mui-expanded {
      margin: 0;
    }
  }
`;

export const StyledAccordionSummary = styled(AccordionSummary)`
  &.MuiAccordionSummary-root {
    flex-direction: row-reverse;
    padding: 0;
    & .MuiButtonBase-root {
      padding: 0;
      margin-right: 8px;
    }
  }
`;

export const StyledAccordionDetails = styled(AccordionDetails)`
  width: 100%;
`;

export const StyledPaymentItemBox = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  padding: 16px 24px;
  background-color: ${THEME.greyColors.grey12};
  border-bottom: 1px solid ${THEME.greyColors.grey5};
  }
  &:last-child {
    border-bottom: none;
  }
`;

export {
  StyledFormControl,
  StyledSelect,
  ConditionStyledFormControl,
  StyledCopyToClipboard,
  StyledCopyValue,
  StyledAddNewButton,
  StyledBox,
  InfoLinkBox,
  ActionOptionBox,
  RemoveActionBox,
  AddActionButton,
  ActionTextField,
  PromptTextField,
  StyledFormControlLabel,
  useStyles,
};
